import { z } from 'zod';

/**
 * 人员与卡上责任（C-167 A5）。
 *
 * ## 加人是两步，且必须在一个事务里
 *
 * 一个人上到一张卡上，库层要落两行：`person`（人员本体）与 `atc_person`（卡上责任）。
 * **只做第一步**，人进了库但哪张卡都不挂——他不出现在任何卡的人数里，
 * 而人员台账里又确实有这个人，于是「人数对不上」而没有任何地方报错。
 * **只做第二步**做不到：`atc_person.person_ref` 有外键指向 `person(employee_no)`。
 *
 * 所以接口只有一个动作：**挂人到卡上**。它自己决定要不要先建人：
 * - 传 `employeeNo` → 那个人必须已存在，只落第二行
 * - 传 `name`（不传 `employeeNo`）→ 取号建人，两行一起落
 *
 * 没有「只建人不挂卡」的端点。人员台账（第四张台账）另立卡，届时再开。
 *
 * ## 人数没有录入口
 *
 * 人数是 `person` / `atc_person` 数出来的（视图 `v_atc_person_count`），
 * **不是一个可填字段**。故本契约里没有任何 `headcount` 之类的入参——
 * 一旦有，就会有人去填它，而填进去的数与数出来的数必然分叉。
 */

/** 卡上角色三类（`atc_person.role` 的 CHECK 原样）。 */
export const atcPersonRoleSchema = z.enum(['responsible', 'user', 'evaluator']);
export type AtcPersonRole = z.infer<typeof atcPersonRoleSchema>;

const employeeNoSchema = z
  .string()
  .trim()
  .regex(/^[A-Z]{1,4}[0-9]{4,}$/, '工号形如 E0001：1 至 4 位大写字母 + 至少 4 位数字');

/**
 * 挂人到卡上。
 *
 * `.strict()`：多传的字段变 400 而**不是被静默忽略**。
 * 被忽略最坏——调用方以为设了某个字段，实际没设，而请求还是 201。
 */
export const attachAtcPersonSchema = z
  .object({
    role: atcPersonRoleSchema,
    effectiveFrom: z.string().date(),
    /** 已有的人：给工号。与 `name` 二选一。 */
    employeeNo: employeeNoSchema.optional(),
    /** 新人：给姓名，工号由库取号（`allocate_person_no`）。 */
    name: z.string().trim().min(1).max(100).optional(),
    /** 新人才用得上：工号前缀，1 至 4 位大写字母。 */
    employeeNoPrefix: z
      .string()
      .trim()
      .regex(/^[A-Z]{1,4}$/, '工号前缀为 1 至 4 位大写字母')
      .optional(),
    /** 新人才用得上：入职日。不传取 `effectiveFrom`。 */
    employedFrom: z.string().date().optional(),
    /** 客户侧标识（ADR-0030 十）。业务层，可空——**不得反过来当锚点**。 */
    externalRef: z.string().trim().max(200).optional(),
  })
  .strict()
  .refine(
    (v) => (v.employeeNo === undefined) !== (v.name === undefined),
    '要么给已有的工号，要么给新人的姓名——两个都给或都不给，无法判断是挂已有的人还是建新人',
  )
  .refine(
    (v) => v.employeeNo === undefined || (v.employeeNoPrefix === undefined && v.employedFrom === undefined),
    '挂已有的人时不接受工号前缀与入职日：那两项属于建人，人已经在了',
  );

export type AttachAtcPersonInput = z.infer<typeof attachAtcPersonSchema>;

export const atcPersonSchema = z.object({
  atcPersonId: z.string(),
  atcId: z.string(),
  employeeNo: z.string(),
  name: z.string(),
  role: atcPersonRoleSchema,
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  enabled: z.boolean(),
  /** 这一次调用有没有顺带建了人。调用方据此知道两步都发生了，还是只发生了第二步。 */
  personCreated: z.boolean(),
});
export type AtcPerson = z.infer<typeof atcPersonSchema>;

/** 这张卡有多少人可用（视图 `v_atc_person_count`）。**统计值，不可填**。 */
export const atcPersonCountSchema = z.object({
  atcId: z.string(),
  personCount: z.number().int(),
  responsibleCount: z.number().int(),
  userCount: z.number().int(),
  evaluatorCount: z.number().int(),
});
export type AtcPersonCount = z.infer<typeof atcPersonCountSchema>;

export const atcPersonListSchema = z.object({
  code: z.string(),
  count: atcPersonCountSchema,
  rows: z.array(atcPersonSchema.omit({ personCreated: true })),
  /**
   * 卡的负责人（`atc.owner_person_no`，本体档，唯一可变更；C-190 批一）。
   * 与 rows 里的 responsible（使用责任，每次执行落一名）**不是同一件事**。
   * null = 尚未指派（零态）。positionName 是负责人当前所在岗位的名字。
   */
  owner: z
    .object({
      personNo: z.string(),
      name: z.string(),
      positionName: z.string().nullable(),
    })
    .nullable(),
  /** true = owner 是回填/批量指派推出来的，不是指派人显式给的（推断值必须可识别）。 */
  ownerInferred: z.boolean(),
});
export type AtcPersonList = z.infer<typeof atcPersonListSchema>;

/* ═══════ 人员台账（第四张台账，C-169 A5） ═══════ */

/**
 * 在岗关系的一段区间（`person_assignment` 的一行）。
 *
 * **调岗不是「改所属岗位」**，是闭合旧区间 + 追加新区间，一个事务（ADR-0030 七）。
 * 所以历史是一串区间，不是一个被覆盖掉的字段。
 *
 * 台账要能查这串历史：「上季度是什么岗」是 AI 替代率趋势的前提——
 * 只留当前区间的话，上一季度的分母永远算不回来。
 *
 * 半开区间 `[validFrom, validTo)`：上一段截止即下一段起始，不留空档，也不重叠
 * （库级 EXCLUDE，ADR-0030 八）。
 */
export const personAssignmentSchema = z.object({
  positionId: z.string(),
  positionName: z.string(),
  departmentId: z.string().nullable(),
  departmentName: z.string().nullable(),
  validFrom: z.string(),
  /** `null` = 仍在这一段里，即当前岗位。 */
  validTo: z.string().nullable(),
});
export type PersonAssignment = z.infer<typeof personAssignmentSchema>;

/**
 * 人员台账的一行。
 *
 * ## 行锚是**工号**，不是「编码 + 名称」
 *
 * 前三张台账（部门 / 岗位 / ATC）的形制是「编码 + 名称」两列并列。**人员不照抄这个形制。**
 * `employee_no` 全局唯一、永不复用、永不 UPDATE（库级触发器强制），
 * 而 `externalRef` 是客户侧标识——业务层，可空可改，**不得反过来充当锚点**
 * （ADR-0030 一、四、十）。
 *
 * 两者并列成两个等价的「编码」列，锚点语义当场就丢了：
 * 读表的人不知道换了人事系统之后哪一个还认得出同一个人。
 * 故契约里两者都给，**界面须标明哪个是锚点**。
 *
 * ## 「所属岗位」是在岗关系当前区间的**投影**，不是人身上的字段
 *
 * 人与岗位的关系存在 `person_assignment`——带区间的仅追加表。
 * 「他在哪个岗位」这句话必须补一个「什么时候」才完整；`positionId` 答的是**当下**。
 *
 * 因此它**只读**：改它没有意义，能改的只有「再追加一段」。
 * 库级 `person_assignment_no_overlap`（EXCLUDE，半开区间）保证同一工号在同一时刻
 * 至多一条——所以这里是单值而不是数组，且这一点是库保的，不是这里选的。
 * 一人不可多岗是 V1 的既定实现（ADR-0030 八：兼岗会让日编制工时重复计算、
 * AI 替代率分母静默虚高且无处报错）。
 *
 * ## 人员**没有** active / suspended / retired 那一套
 *
 * 只有在职与离职，由 `employedTo` 一个字段表达：`null` = 在职。
 * 且它是**一次性写入**——null → 非 null 走一次，此后库级触发器拒绝再改（ADR-0030 三）。
 *
 * 所以不存在「人员归档」「人员恢复」这类动作，契约里也没有对应字段。
 * 数据层根本不支持，做出来就是骗人。
 *
 * ## 关联 ATC 给的是 **id 列表**，不是一个数
 *
 * 台账要显示「关联 ATC 数」，而收窄到某张卡时还要问「这张卡上有谁」——
 * 给数就答不了后一问，两处各查一遍就是同一个判断的第二处实现。
 * 故只给列表，数由 `atcIds.length` 得来，**没有第二个可以对不上的字段**。
 */
export const personLedgerRowSchema = z.object({
  /** **锚点。**全局唯一、永不复用、永不改（ADR-0030 一、四）。 */
  employeeNo: z.string(),
  name: z.string(),
  /** 客户侧标识。业务层，可空可改，**不是锚点**（ADR-0030 十）。 */
  externalRef: z.string().nullable(),
  employedFrom: z.string(),
  /** `null` = 在职。非 null = 离职日，且此后不可再改（ADR-0030 三）。 */
  employedTo: z.string().nullable(),

  /** 当下在岗岗位；没有覆盖此刻的区间时为 null。**只读投影。** */
  positionId: z.string().nullable(),
  positionName: z.string().nullable(),
  /** 岗位所属部门。`positionId` 为 null 时同为 null——部门是顺着岗位查出来的。 */
  departmentId: z.string().nullable(),
  departmentName: z.string().nullable(),

  /** 在岗历史：全部区间，**按起始降序**（最近的在前）。含当前那一段。 */
  assignments: z.array(personAssignmentSchema),

  /** 当下挂在这个人身上的卡。**顺序按卡编号**，前端不再排一遍。 */
  atcIds: z.array(z.string()),
});
export type PersonLedgerRow = z.infer<typeof personLedgerRowSchema>;

export const personLedgerSchema = z.object({
  rows: z.array(personLedgerRowSchema),
});
export type PersonLedger = z.infer<typeof personLedgerSchema>;

/* ═══════ 人员台账的写路径（C-181 A4/A5） ═══════
 *
 * 四个动作，各司其职：**没有一个是「改 person 行的岗位」**——
 * 人与岗位的关系在 `person_assignment` 的区间里，改它只有「再追加一段」。
 *
 * | 动作 | 端点 | 事务 |
 * |---|---|---|
 * | 新增（建人 + 挂岗） | POST /api/org/people | 取号、建人、挂岗同一事务 |
 * | 修改（姓名 / 客户侧标识） | PATCH /api/org/people/:employeeNo | 单行更新 |
 * | 调岗 | POST /api/org/people/:employeeNo/assignment | 闭合旧区间 + 追加新区间 |
 * | 离职 | POST /api/org/people/:employeeNo/departure | 写 employed_to + 闭合在岗区间 |
 *
 * 工号是锚点：任何入参都不收它（建人时由库取号）。「人员归档」「人员恢复」
 * 不存在——人员只有在职 / 离职（`employed_to` 一次性写入，ADR-0030 三）。
 */

/** 新增人员：建人 + 挂岗。*/
export const createPersonSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    /** 客户侧标识。业务层，可空——**不得反过来当锚点**（ADR-0030 十）。 */
    externalRef: z.string().trim().max(200).optional(),
    /** 工号前缀，1 至 4 位大写字母。不传用 E。 */
    employeeNoPrefix: z
      .string()
      .trim()
      .regex(/^[A-Z]{1,4}$/, '工号前缀为 1 至 4 位大写字母')
      .optional(),
    /** 入职日，同时是首段在岗区间的起点。不传取业务日（库算）。 */
    employedFrom: z.string().date().optional(),
    /** 首个岗位。一人一岗（EXCLUDE，半开区间），没有「先建人不挂岗」。 */
    positionId: z.string().min(1, '请选择所属岗位'),
  })
  .strict();
export type CreatePersonInput = z.infer<typeof createPersonSchema>;

/** 修改人员：只有姓名与客户侧标识。工号不可改（锚点）；岗位不在这里（调岗）。 */
export const updatePersonSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    /** 显式传 null 即清除。 */
    externalRef: z.string().trim().max(200).nullable().optional(),
  })
  .strict();
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>;

/** 调岗：闭合当前区间 + 追加新区间，两步一个事务。 */
export const reassignPersonSchema = z
  .object({
    positionId: z.string().min(1, '请选择新岗位'),
    /** 新区间起点，同时是旧区间的闭合点（半开 [)：相邻不算重叠）。 */
    validFrom: z.string().date(),
  })
  .strict();
export type ReassignPersonInput = z.infer<typeof reassignPersonSchema>;

/** 离职：`employed_to` 一次性写入（null → 非 null，库级触发器强制）。 */
export const departPersonSchema = z
  .object({
    employedTo: z.string().date(),
  })
  .strict();
export type DepartPersonInput = z.infer<typeof departPersonSchema>;
